import React from 'react';
import { motion } from 'framer-motion';
import { useSimulation } from '../context/SimulationContext';
import { HUDMeters } from './HUDMeters';
import { PrepCardSummary } from './PrepCardBuilder';
import { Button } from './ui/button';
import { 
  LayoutDashboard, 
  BookOpen, 
  Play, 
  BarChart3,
  ChevronRight,
  Clock,
  Target,
  Users,
  FileText,
  Home,
} from 'lucide-react';

// Stagger animation variants
const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08 } },
};
const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.25, 0.1, 0.25, 1] } },
};

const NAV_ITEMS = [
  { id: 'hub', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'theory', label: 'Conversation Tools', icon: BookOpen },
  { id: 'prep', label: 'Conversation Plan', icon: FileText },
  { id: 'simulation', label: 'Simulation', icon: Play, disabled: true },
  { id: 'dashboard', label: 'Debrief', icon: BarChart3, disabled: true },
];

export function GlobalHub({ onGoHome }) {
  const { state, setView } = useSimulation();
  const { currentView, prepCard } = state;

  const canEnterSimulation = prepCard.completed;

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col lg:grid lg:grid-cols-12" data-testid="global-hub">
      {/* Left Navigation - Navy Shell */}
      <aside className="lg:col-span-2 bg-[#002B45] text-white p-3 lg:p-6 flex items-center lg:items-stretch lg:flex-col lg:min-h-screen" data-testid="hub-nav">
        {/* Logo */}
        <div className="lg:mb-10 flex-shrink-0 mr-2 lg:mr-0">
          <h1 className="text-base lg:text-lg font-headline font-bold tracking-tight whitespace-nowrap">
            Leader Cadence
          </h1>
          <p className="text-[10px] uppercase tracking-widest text-slate-400 mt-1 hidden lg:block">
            Decision Rehearsal
          </p>
        </div>

        {/* Navigation */}
        <nav className="flex lg:flex-col lg:space-y-1 lg:flex-1 ml-auto lg:ml-0 gap-0.5 lg:gap-0">
          {NAV_ITEMS.map(item => {
            const Icon = item.icon;
            const isActive = currentView === item.id;
            const isDisabled = item.disabled && !canEnterSimulation;
            
            return (
              <button
                key={item.id}
                onClick={() => !isDisabled && setView(item.id)}
                disabled={isDisabled}
                title={item.label}
                className={`flex items-center justify-center lg:justify-start gap-3 p-2 lg:px-4 lg:py-3 text-left transition-all ${
                  isActive 
                    ? 'nav-active-bar bg-white/[0.06] text-white' 
                    : isDisabled
                      ? 'text-slate-600 cursor-not-allowed'
                      : 'text-slate-400 hover:bg-white/[0.04] hover:text-white'
                }`}
                data-testid={`nav-${item.id}`}
              >
                <Icon className={`w-4 h-4 flex-shrink-0 ${isActive ? 'text-[#00A9E0]' : ''}`} strokeWidth={1.5} />
                <span className="hidden lg:inline text-sm font-medium">{item.label}</span>
              </button>
            );
          })}

          {/* Home / Restart — last nav item */}
          {onGoHome && (
            <button
              onClick={onGoHome}
              title="Restart course"
              className="flex items-center justify-center lg:justify-start gap-3 p-2 lg:px-4 lg:py-3 text-left text-slate-500 hover:bg-white/[0.04] hover:text-white transition-all lg:mt-auto"
              data-testid="nav-home"
            >
              <Home className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} />
              <span className="hidden lg:inline text-sm font-medium">Restart</span>
            </button>
          )}
        </nav>

        {/* Footer Links */}
        <div className="hidden lg:block pt-6">
          <div className="divider-glow mb-4" />
          <p className="text-[10px] uppercase tracking-widest text-slate-500 px-4">
            Leader Cadence v1.0
          </p>
        </div>
      </aside>

      {/* Agenda Card */}
      <div className="hidden lg:block lg:col-span-3 bg-[#F1F5F9] p-6 space-y-6" data-testid="hub-agenda">
        {/* Current Module */}
        <div className="mb-8">
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-2">
            Current Module
          </p>
          <h2 className="text-xl font-headline font-bold text-[#002B45]">
            A Strained Conversation
          </h2>
        </div>

        {/* Module Progress */}
        <div className="bg-white p-5 shadow-ambient">
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">
              Progress
            </span>
            <span className="text-xs font-bold text-[#0055A5]">
              {prepCard.completed ? '100%' : prepCard.openingStance ? '50%' : '0%'}
            </span>
          </div>
          
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <div className={`w-4 h-4 flex items-center justify-center ${
                true ? 'bg-[#78BE20]' : 'bg-slate-200'
              }`}>
                {true && <span className="text-white text-xs">✓</span>}
              </div>
              <span className="text-sm text-[#002B45]">Theory Review</span>
            </div>
            
            <div className="flex items-center gap-3">
              <div className={`w-4 h-4 flex items-center justify-center ${
                prepCard.completed ? 'bg-[#78BE20]' : 'bg-slate-200'
              }`}>
                {prepCard.completed && <span className="text-white text-xs">✓</span>}
              </div>
              <span className="text-sm text-[#002B45]">Plan</span>
            </div>
            
            <div className="flex items-center gap-3">
              <div className={`w-4 h-4 flex items-center justify-center ${
                state.outcome ? 'bg-[#78BE20]' : 'bg-slate-200'
              }`}>
                {state.outcome && <span className="text-white text-xs">✓</span>}
              </div>
              <span className="text-sm text-[#002B45]">Simulation</span>
            </div>
          </div>
        </div>

        {/* Leadership Instruments */}
        <div className="bg-white p-5 shadow-ambient">
          <HUDMeters />
        </div>

        {/* Prep Card Summary */}
        {(prepCard.openingStance || prepCard.completed) && (
          <PrepCardSummary compact />
        )}
      </div>

      {/* Main Content Panel */}
      <motion.main
        initial="hidden"
        animate="visible"
        variants={stagger}
        className="flex-1 lg:col-span-7 p-6 sm:p-8 lg:p-12"
        data-testid="hub-content"
      >
        {/* Welcome Header */}
        <motion.div variants={fadeUp} className="mb-12">
          <p className="text-xs font-semibold uppercase tracking-widest text-[#0055A5] mb-2">
            Today's Rehearsal
          </p>
          <p className="text-lg text-[#475569] max-w-2xl">
            Practice a difficult leadership conversation before you have to lead one in real life.
          </p>
        </motion.div>

        {/* Scenario Brief */}
        <motion.div variants={fadeUp} className="bg-white p-4 sm:p-6 lg:p-8 shadow-ambient mb-8 overflow-hidden" data-testid="scenario-brief">
          <div className="flex items-start gap-4 sm:gap-6">
            <div className="hidden sm:flex w-16 h-16 bg-[#002B45] items-center justify-center flex-shrink-0">
              <Users className="w-8 h-8 text-white" strokeWidth={1.5} />
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-semibold uppercase tracking-widest text-[#0055A5]">
                  Active Scenario
                </span>
                <span className="text-xs font-medium text-slate-400">•</span>
                <span className="text-xs text-slate-400 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  ~15 min
                </span>
              </div>
              
              <p className="text-sm sm:text-base text-[#475569] leading-relaxed mb-4">
                A parent has raised a concern that a student felt embarrassed in Ms. Ortega's class and has become 
                reluctant to participate. Ms. Ortega knows leadership has received the concern. The facts are incomplete. 
                Your task is to understand what happened, name what matters, and move toward a fair next step.
              </p>

              <div className="flex flex-wrap gap-2 sm:gap-3">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-[#002B45] bg-slate-100 px-3 py-1">
                  Parent Complaint
                </span>
                <span className="text-[10px] font-semibold uppercase tracking-wider text-[#002B45] bg-slate-100 px-3 py-1">
                  Teacher Support
                </span>
                <span className="text-[10px] font-semibold uppercase tracking-wider text-[#002B45] bg-slate-100 px-3 py-1">
                  Student Impact
                </span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Action Cards */}
        <motion.div variants={fadeUp} className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
          {/* Conversation Tools Card */}
          <button
            onClick={() => setView('theory')}
            className="bg-white p-6 shadow-ambient hover:shadow-ambient-strong hover:glow-blue-sm transition-all text-left group"
            data-testid="action-theory"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="p-3 bg-[#0055A5]/10 group-hover:bg-[#0055A5] transition-colors">
                <BookOpen className="w-5 h-5 text-[#0055A5] group-hover:text-white transition-colors" strokeWidth={1.5} />
              </div>
              <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-[#00A9E0] transition-colors" />
            </div>
            
            <h4 className="text-lg font-headline font-semibold text-[#002B45] mb-2">
              Conversation Tools
            </h4>
            <p className="text-sm text-[#475569]">
              Review the key moves that shape trust, clarity, and accountability.
            </p>
          </button>

          {/* Build Plan Card */}
          <button
            onClick={() => setView('prep')}
            className="bg-white p-6 shadow-ambient hover:shadow-ambient-strong hover:glow-blue-sm transition-all text-left group"
            data-testid="action-prep"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="p-3 bg-[#0055A5]/10 group-hover:bg-[#0055A5] transition-colors">
                <Target className="w-5 h-5 text-[#0055A5] group-hover:text-white transition-colors" strokeWidth={1.5} />
              </div>
              <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-[#00A9E0] transition-colors" />
            </div>
            
            <h4 className="text-lg font-headline font-semibold text-[#002B45] mb-2">
              Build Your Plan
            </h4>
            <p className="text-sm text-[#475569]">
              Clarify how you want to enter the conversation before you begin.
            </p>
          </button>
        </motion.div>

        {/* Enter Simulation CTA */}
        {prepCard.completed && (
          <motion.div variants={fadeUp} className="mt-8 p-6 bg-[#0055A5] text-white glow-blue" data-testid="simulation-cta">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-lg font-headline font-semibold mb-1">
                  Ready to Enter Simulation
                </h4>
                <p className="text-sm text-blue-100">
                  Your plan is complete. Begin the conversation with Ms. Ortega.
                </p>
              </div>
              
              <Button
                onClick={() => setView('simulation')}
                className="bg-white text-[#0055A5] hover:bg-slate-100 px-6"
                data-testid="enter-simulation-btn"
              >
                <Play className="w-4 h-4 mr-2" />
                Enter Simulation
              </Button>
            </div>
          </motion.div>
        )}
      </motion.main>
    </div>
  );
}

export default GlobalHub;
